import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/pro-regular-svg-icons';
import { Input } from '@design-system/components/input/input';
import { FormLabel } from '@design-system/components/grid/grid';
import { Typography } from '@design-system/components/typography/typography';
import { ColorPicker } from '@design-system/components/colorPicker/colorPicker';
import { RHFSelect } from '@client/new/components/rhfSelect';
import MediaUpload from '@client/new/components/MediaUpload';
import * as mediaStyles from './MediaSection.css';
import { FormLabelSuperTooltip } from './FormLabelSuperTooltip';
import { Option } from '@client/new/helpers/types';
import { ProfileVM } from '../../view-model/Profile.viewModel';
import { ProfileFormValues } from '../../view-model/Profile.schema';
import * as styles from './IdentitySection.css';

type IdentitySectionProps = {
  vm: ProfileVM;
};

const genderOptions: Option<ProfileFormValues['gender']>[] = [
  { value: null, label: 'Select' },
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
];

export const IdentitySection = ({ vm }: IdentitySectionProps) => {
  const { form } = vm;
  const primaryColor = form.watch('customColorSchema.primaryColor') || '000000';
  const mediaAvatar = form.watch('mediaAvatar');
  const mediaCover = form.watch('mediaCover');

  return (
    <div className={styles.fieldStack}>
      <div className={styles.mediaRow}>
        <div className={styles.avatarUpload}>
          <FormLabelSuperTooltip templateId="profile-avatar-image" required>
            Avatar Image
          </FormLabelSuperTooltip>
          <div className={mediaStyles.uploadWrapper}>
            <MediaUpload
              id="avatar"
              creatorId={vm.profile.id}
              accept={['image/png', 'image/jpeg']}
              className={styles.avatarCard}
              value={mediaAvatar || null}
              onChange={(metadata) => {
                form.setValue(
                  'mediaAvatar',
                  metadata as ProfileFormValues['mediaAvatar'],
                  { shouldDirty: true },
                );
              }}
            >
              <div className={mediaStyles.mediaInfoText}>
                <span className={mediaStyles.mediaInfoTitle}>Avatar</span>
                <span className={mediaStyles.mediaInfoSubtitle}>
                  1080 x 1080
                </span>
              </div>
            </MediaUpload>
            {!mediaAvatar && (
              <div className={mediaStyles.editBadge}>
                <FontAwesomeIcon icon={faPen} />
              </div>
            )}
          </div>
          {typeof form.errors.mediaAvatar?.message === 'string' && (
            <Typography size="small" color="ERROR-100">
              {form.errors.mediaAvatar.message}
            </Typography>
          )}
        </div>

        <div className={styles.coverUpload}>
          <FormLabelSuperTooltip templateId="cover-image">
            Cover image
          </FormLabelSuperTooltip>
          <div className={mediaStyles.uploadWrapper}>
            <MediaUpload
              id="cover-photo"
              creatorId={vm.profile.id}
              accept={['image/png', 'image/jpeg']}
              className={styles.coverCard}
              value={mediaCover || null}
              onChange={(metadata) => {
                form.setValue(
                  'mediaCover',
                  metadata as ProfileFormValues['mediaCover'],
                  { shouldDirty: true },
                );
              }}
            >
              <div className={mediaStyles.mediaInfoText}>
                <span className={mediaStyles.mediaInfoTitle}>
                  Shown in Discovery
                </span>
                <span className={mediaStyles.mediaInfoSubtitle}>
                  1080 x 1080
                </span>
              </div>
            </MediaUpload>
            {!mediaCover && (
              <div className={mediaStyles.editBadge}>
                <FontAwesomeIcon icon={faPen} />
              </div>
            )}
          </div>
          {typeof form.errors.mediaCover?.message === 'string' && (
            <Typography size="small" color="ERROR-100">
              {form.errors.mediaCover.message}
            </Typography>
          )}
        </div>

        <div className={styles.colorPickerWrapper}>
          <FormLabel>Channel color</FormLabel>
          <ColorPicker
            value={primaryColor}
            onChange={(hex) => {
              form.setValue('customColorSchema.primaryColor', hex, {
                shouldDirty: true,
              });
            }}
            error={form.errors.customColorSchema?.primaryColor?.message}
          />
        </div>
      </div>

      <div className={styles.nameRow}>
        <Input
          {...form.register('firstName')}
          label="First Name"
          required
          error={form.errors.firstName?.message}
        />
        <Input
          {...form.register('lastName')}
          label="Last Name"
          required
          error={form.errors.lastName?.message}
        />
      </div>

      <div>
        <FormLabelSuperTooltip templateId="profile-title" required>
          Title
        </FormLabelSuperTooltip>
        <Input
          {...form.register('title')}
          placeholder="E.g Strength Coach, Mindfulness & Yoga instructor"
          error={form.errors.title?.message}
        />
      </div>

      <div>
        <FormLabel>Gender</FormLabel>
        <RHFSelect<ProfileFormValues>
          name="gender"
          control={form.control}
          options={genderOptions}
        />
      </div>
    </div>
  );
};
